import { Injectable, inject } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { FormsService } from './forms.service';
import { NomSupportedLanguages } from '../constants';
import { OutputSettingsFormat } from '../models/output-settings.models';

export interface TransformationContext {
  showComponents: boolean; // * Layout elements section
  showProperties: boolean; // * Properties section
  showSettings: boolean; // *   Settings section
  showTemplates: boolean; // *  Template controls
}

@Injectable({
  providedIn: 'root',
})
export class OutputSettingsTransformationService {
  // * Service for transforming Output Settings between:
  // * Backend API Format  -> Form Controls
  // * Form Controls       -> Backend API Format
  // * used by both Feed and Widget contexts.

  private formsService = inject(FormsService);

  // * transformation context -> Feed-level Output Settings
  createFeedContext(): TransformationContext {
    return {
      showComponents: true,
      showProperties: true,
      showSettings: true,
      showTemplates: true,
    };
  }

  // * transformation context -> Widget-level Output Settings
  createWidgetContext(): TransformationContext {
    return {
      showComponents: false,
      showProperties: true,
      showSettings: true,
      showTemplates: false,
    };
  }

  // ! Backend API Format -> Form Controls
  transformApiToForm(
    format: OutputSettingsFormat,
    context: TransformationContext,
  ): FormGroup {
    const formConfig: any = {};

    // ! [Number of Posts control] - common to both contexts
    formConfig.number_of_posts = [format.number_of_posts || 100];

    // * [Template controls] - Feed only
    if (context.showTemplates && format.has_templates) {
      NomSupportedLanguages.forEach((language) => {
        formConfig[`template_${language.id}`] = [
          format.templates?.[language.id]?.id || '',
        ];
      });
    }

    // ! [Layout controls] - Feed only
    if (context.showComponents) {
      if (format.components) {
        const sortedComponents = [...format.components].sort(
          (a, b) => a.index - b.index,
        );
        sortedComponents.forEach((component) => {
          const elementId = this.getLayoutElementId(component.name);
          formConfig[`layout_${elementId}_selected`] = [component.selected];
          formConfig[`layout_${elementId}_order`] = [component.index];
        });
      } else {
        // * Fallback default layout elements
        const defaultElements = [
          { id: 'table_of_contents', selected: true, order: 0 },
          { id: 'widgets', selected: true, order: 1 },
          { id: 'content', selected: true, order: 2 },
        ];
        defaultElements.forEach((element) => {
          formConfig[`layout_${element.id}_selected`] = [element.selected];
          formConfig[`layout_${element.id}_order`] = [element.order];
        });
      }
    }

    // ! [Properties controls] - both contexts when enabled
    if (context.showProperties && format.properties) {
      format.properties.forEach((property) => {
        const controlName = `property_${property.key}_value`;
        formConfig[controlName] = [property.selected || false];
        // list-type property with values
        if (property.values && property.values.length > 0) {
          const selectedControlName = `property_${property.key}_property_value`;
          formConfig[selectedControlName] = [property.value || false];
        }
        // per-property options (e.g. limit, sort) - backend sends resolved defaults in option_values
        if (property.options && property.options.length > 0) {
          property.options.forEach((option) => {
            const optionControlName = `property_${property.key}_option_${option.key}`;
            const initialValue =
              property.option_values?.[option.key] ?? option.default ?? null;
            formConfig[optionControlName] = [initialValue];
          });
        }
      });
    }

    // ! [Settings controls] - Feed context only (later Widget also will use this)
    if (format.settings) {
      format.settings.forEach((settingGroup) => {
        if (settingGroup.settings) {
          settingGroup.settings.forEach((setting) => {
            let controlName = '';
            let defaultValue: any = false;

            // ! Feed uses complex switch logic
            // ! Widget uses simplified logic
            switch (setting.value_type) {
              case 'TOGGLE':
                controlName = `setting_${settingGroup.id}_${setting.id}_value`;
                defaultValue = setting.selected || false;
                break;
              case 'LIST':
                controlName = `setting_${settingGroup.id}_${setting.id}_value`;
                defaultValue = setting.value || null;
                break;
              case 'INPUT_NUMBER':
                controlName = `setting_${settingGroup.id}_${setting.id}_value`;
                defaultValue = setting.value || 1;
                break;
            }

            if (controlName) {
              formConfig[controlName] = [defaultValue];
            }
          });
        }
      });
    }

    return this.formsService.formBuilder.group(formConfig);
  }

  // ! Form Controls -> Backend API Format
  transformFormToApi(
    formGroup: FormGroup,
    format: OutputSettingsFormat,
    context: TransformationContext,
  ): void {
    // * Basic fields
    const numberOfPostsControl = formGroup.get('number_of_posts');
    if (numberOfPostsControl) {
      format.number_of_posts = numberOfPostsControl.value;
    }

    // ! [Templates field]: template_1 -> templates["1"].template_path (Feed only)
    if (context.showTemplates && format.has_templates) {
      NomSupportedLanguages.forEach((language) => {
        format.templates[language.id] ??= {
          id: 0,
          template_path: null,
          file_path: null,
          language_id: language.id,
          name: null,
        };

        const templateControl = formGroup.get(`template_${language.id}`);
        if (templateControl) {
          // A cleared picker holds null; "no template" has always been sent as ''
          format.templates[language.id].id = templateControl.value || '';
        }
      });
    }

    // ! [Properties field]: property_export_post_title_value -> properties[].selected
    if (context.showProperties && format.properties) {
      format.properties.forEach((property) => {
        const propertyControl = formGroup.get(`property_${property.key}_value`);
        if (propertyControl) {
          property.selected = propertyControl.value;
        }
        // multivalue properties
        const propertyControlValue = formGroup.get(
          `property_${property.key}_property_value`,
        );

        if (propertyControlValue) {
          property.value = propertyControlValue.value;
          if (!format.property_values) {
            format.property_values = {};
          }
          format.property_values[property.key] = propertyControlValue.value;
        }

        // per-property options (e.g. limit, sort)
        if (property.options && property.options.length > 0) {
          const resolvedOptionValues: Record<string, any> = {};
          property.options.forEach((option) => {
            const optionControl = formGroup.get(
              `property_${property.key}_option_${option.key}`,
            );
            if (optionControl) {
              resolvedOptionValues[option.key] = optionControl.value;
            }
          });
          property.option_values = resolvedOptionValues;

          if (!format.property_option_values) {
            format.property_option_values = {};
          }
          format.property_option_values[property.key] = resolvedOptionValues;
        }
      });
    }

    // ! [Settings fields]: setting_1_5_value -> settings[0].settings[0].selected (Feed context)
    if (context.showSettings && format.settings) {
      format.settings.forEach((settingGroup) => {
        settingGroup.settings.forEach((setting) => {
          let controlName = '';

          switch (setting.value_type) {
            case 'TOGGLE':
              controlName = `setting_${settingGroup.id}_${setting.id}_value`;
              const toggleControl = formGroup.get(controlName);
              if (toggleControl) {
                setting.selected = toggleControl.value;
              }
              break;
            case 'LIST':
              controlName = `setting_${settingGroup.id}_${setting.id}_value`;
              const listControl = formGroup.get(controlName);
              if (listControl) {
                setting.value = listControl.value;
              }
              break;
            case 'INPUT_NUMBER':
              controlName = `setting_${settingGroup.id}_${setting.id}_value`;
              const numberControl = formGroup.get(controlName);
              if (numberControl) {
                setting.value = numberControl.value;
              }
              break;
          }
        });
      });
    }

    // ! [Layout field]: layout_widgets_selected -> components[].selected (Feed context)
    if (context.showComponents && format.components) {
      this.updateComponentsFromForm(format, formGroup);
    }
  }

  private updateComponentsFromForm(
    format: OutputSettingsFormat,
    formGroup: FormGroup,
  ): void {
    if (format.components) {
      format.components.forEach((component) => {
        const elementId = this.getLayoutElementId(component.name);

        // * Update selected state
        const selectedControl = formGroup.get(`layout_${elementId}_selected`);
        if (selectedControl && selectedControl.value !== undefined) {
          component.selected = selectedControl.value;
        }

        // * Update order/index
        const orderControl = formGroup.get(`layout_${elementId}_order`);
        if (orderControl && orderControl.value !== undefined) {
          component.index = orderControl.value;
        }
      });
    }
  }

  // * Maps component names to layout element IDs
  getLayoutElementId(componentName: string): string {
    const nameToIdMap: { [key: string]: string } = {
      'Table of Contents': 'table_of_contents',
      Widgets: 'widgets',
      Content: 'content',
    };

    return (
      nameToIdMap[componentName] ||
      componentName.toLowerCase().replace(/\s+/g, '_')
    );
  }
}
